import axios from 'axios';
import React, { useState } from 'react';
import { config } from '../../../../../config';
import imageCompression from 'browser-image-compression';

const CreateInstructor = ({ onWebinarInstructorCreated }) => {
  const apiUrl = config.apiUrl;

  const [formData, setFormData] = useState({
    slug: '',
    image: null,
    headline: '',
    experience: '',
    goal: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isCompressing, setIsCompressing] = useState(false);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsCompressing(true);
    try {
      const options = {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 1024,
        useWebWorker: true,
        fileType: 'image/webp'
      };

      const compressedFile = await imageCompression(file, options);
      setFormData(prev => ({ ...prev, image: compressedFile }));
    } catch (error) {
      console.error('Error compressing image:', error);
      setErrors(prev => ({ ...prev, image: 'Failed to compress image' }));
    } finally {
      setIsCompressing(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'image') {
      handleImageChange(e);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSuccessMessage('');
    setErrors({});

    try {
      const data = new FormData();
      data.append('slug', formData.slug);
      data.append('headline', formData.headline);
      data.append('experience', formData.experience);
      data.append('goal', formData.goal);
      if (formData.image) {
        data.append('image', formData.image);
      }

      const response = await axios.post(`${apiUrl}/webinarinstructors`, data, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSuccessMessage('Instructor created successfully!');
      onWebinarInstructorCreated();
      
      // Reset form after successful submission
      setFormData({
        slug: '',
        image: null,
        headline: '',
        experience: '',
        goal: '',
      });
    } catch (error) {
      console.error('Error creating instructor:', error);
      if (error.response) {
        // Handle server validation errors
        if (error.response.data.errors) {
          setErrors(error.response.data.errors);
        } else {
          setErrors({ server: error.response.data.message || 'Failed to create instructor' });
        }
      } else {
        setErrors({ server: 'Network error. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Create New Instructor</h1>
      
      {successMessage && (
        <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-md">
          {successMessage}
        </div>
      )}
      
      {errors.server && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
          {errors.server}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6" encType="multipart/form-data">
        <div className="grid grid-cols-1 gap-6">
          {/* Slug Field */}
          <div>
            <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
              Slug *
            </label>
            <input
              type="text"
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.slug ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'
              }`}
              placeholder="e.g., john-doe"
            />
            {errors.slug && <p className="mt-1 text-sm text-red-600">{errors.slug}</p>}
          </div>

          {/* Image Field */}
          <div>
            <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-1">
              Image *
            </label>
            <input
              type="file"
              id="image"
              name="image"
              onChange={handleChange}
              accept="image/*"
              className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.image ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'
              }`}
              disabled={isCompressing}
            />
            {isCompressing && <p className="mt-1 text-sm text-blue-600">Compressing image...</p>}
            {errors.image && <p className="mt-1 text-sm text-red-600">{errors.image}</p>}
          </div>

          {/* Headline Field */}
          <div>
            <label htmlFor="headline" className="block text-sm font-medium text-gray-700 mb-1">
              Name*
            </label>
            <input
              type="text"
              id="headline"
              name="headline"
              value={formData.headline}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.headline ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'
              }`}
              placeholder="Danial islam"
            />
            {errors.headline && <p className="mt-1 text-sm text-red-600">{errors.headline}</p>}
          </div>

          {/* Experience Field */}
          <div>
            <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-1">
              Experience *
            </label>
            <textarea
              id="experience"
              name="experience"
              value={formData.experience}
              onChange={handleChange}
              rows="3"
              className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.experience ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'
              }`}
              placeholder="10+ years of experience in web development..."
            ></textarea>
            {errors.experience && <p className="mt-1 text-sm text-red-600">{errors.experience}</p>}
          </div>

          {/* Goal Field */}
          <div>
            <label htmlFor="goal" className="block text-sm font-medium text-gray-700 mb-1">
              Goal *
            </label>
            <textarea
              id="goal"
              name="goal"
              value={formData.goal}
              onChange={handleChange}
              rows="3"
              className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.goal ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'
              }`}
              placeholder="My goal is to help students master web development..."
            ></textarea>
            {errors.goal && <p className="mt-1 text-sm text-red-600">{errors.goal}</p>}
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting || isCompressing}
            className={`px-6 py-2 rounded-md text-white font-medium ${
              isSubmitting || isCompressing
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
            }`}
          >
            {isSubmitting ? 'Creating...' : 'Create Instructor'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateInstructor;